import { AsyncPipe } from '@angular/common';
import { Component, inject, input } from '@angular/core';
import type { FormControl, NonNullableFormBuilder } from '@angular/forms';
import { ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import {
  OnyxDropdownComponent,
  OnyxInputLabelComponent,
  OnyxToggleComponent,
} from '@onyx/angular';
import { chain } from 'lodash';
import type { VehicleCategory } from '../../../../dashboard/fleet/common/constants/vehicle-categories';
import { CalculateRouteFeature } from '../../../enums/calculate-route-feature';
import { DictionaryCode } from '../../../enums/dictionary-code';
import type { RoutePreferences } from '../../../interfaces/route/route-preferences';
import { DictionariesService } from '../../../services/dictionaries.service';

export type RoutePreferencesFormGroup = ReturnType<
  typeof RoutePreferencesFormComponent.buildForm
>;
export type RoutePreferencesForm = ReturnType<
  RoutePreferencesFormGroup['getRawValue']
>;

@Component({
  selector: 'app-route-preferences-form',
  imports: [
    OnyxDropdownComponent,
    OnyxInputLabelComponent,
    OnyxToggleComponent,
    ReactiveFormsModule,
    AsyncPipe,
  ],
  templateUrl: './route-preferences-form.component.html',
  styleUrl: './route-preferences-form.component.scss',
})
export class RoutePreferencesFormComponent {
  private readonly dictionariesService = inject(DictionariesService);
  private readonly translateService = inject(TranslateService);

  public readonly form = input.required<RoutePreferencesFormGroup>();

  protected readonly DictionaryCode = DictionaryCode;

  protected readonly FEATURES = chain(CalculateRouteFeature)
    .orderBy((feature) =>
      this.translateService.instant(
        `${DictionaryCode.CALCULATE_ROUTE_FEATURE}.${feature}`,
      ),
    )
    .value();

  protected readonly countries$ = this.dictionariesService.getDictionary(
    DictionaryCode.COUNTRY,
  );

  public static fromDto(dto: Partial<RoutePreferences>): RoutePreferencesForm {
    return {
      vehicleCategory: dto.vehicleCategory ?? null,
      excludeCountries: dto.excludeCountries?.length
        ? dto.excludeCountries
        : null,
      avoidFeatures: chain(CalculateRouteFeature)
        .values()
        .map((feature) => [
          feature,
          dto.avoidFeatures?.includes(feature) ?? false,
        ])
        .fromPairs()
        .value() as Record<CalculateRouteFeature, boolean>,
    };
  }

  public static toDto(form: RoutePreferencesForm): RoutePreferences {
    return {
      ...form,
      avoidFeatures: chain(form.avoidFeatures)
        .toPairs()
        .filter(([, value]) => value)
        .map(([key]) => key as CalculateRouteFeature)
        .orderBy((key) => Object.values(CalculateRouteFeature).indexOf(key))
        .value(),
    } as RoutePreferences;
  }

  public static buildForm(
    fb: NonNullableFormBuilder,
    category: VehicleCategory | null,
  ) {
    return fb.group({
      vehicleCategory: fb.control(category, [Validators.required]),
      excludeCountries: fb.control<string[] | null>(null),
      avoidFeatures: fb.group(
        chain(CalculateRouteFeature)
          .values()
          .map((feature) => [
            feature,
            fb.control<boolean>(false, [Validators.required]),
          ])
          .fromPairs()
          .value() as Record<CalculateRouteFeature, FormControl<boolean>>,
      ),
    });
  }
}
